'use client'

import React from 'react'

type Props = {
    sideA: number
    sideB: number
    sideC: number
}

const WIDTH = 340
const HEIGHT = 220

// Math.cos/sin/acos aren't guaranteed bit-identical between Node (SSR) and
// the browser (CSR) - rounding coordinates avoids a hydration mismatch from
// a 1-ULP-different float producing a different toString() on each side.
const r2 = (n: number) => Math.round(n * 100) / 100

export default function TriangleChart({ sideA, sideB, sideC }: Props) {
    // Place the triangle using sides a (bottom, B-C) and b (left, A-C), with
    // the included angle at C derived from the law of cosines - purely for a
    // reasonably-proportioned diagram, not part of the calculation itself.
    const a = Math.max(sideA, 0.0001)
    const b = Math.max(sideB, 0.0001)
    const c = Math.max(sideC, 0.0001)

    const cosC = Math.min(Math.max((a * a + b * b - c * c) / (2 * a * b), -1), 1)
    const angleC = Math.acos(cosC)

    const scale = 130 / Math.max(a, b, c)
    const bs = b * scale
    const as = a * scale

    const Cx = 80, Cy = 170
    const Bx = Cx + as, By = Cy
    const Ax = r2(Cx + bs * Math.cos(angleC)), Ay = r2(Cy - bs * Math.sin(angleC))

    return (
        <svg width={WIDTH} height={HEIGHT} viewBox={`0 0 ${WIDTH} ${HEIGHT}`} style={{ maxWidth: '100%', height: 'auto' }}>
            <polygon points={`${Ax},${Ay} ${Bx},${By} ${Cx},${Cy}`} fill="#2563eb" opacity={0.18} stroke="#2563eb" strokeWidth={2} />
            <text x={(Bx + Cx) / 2} y={Cy + 20} fontSize={12} textAnchor="middle" fill="#374151">a = {sideA.toFixed(2)}</text>
            <text x={(Ax + Cx) / 2 - 18} y={(Ay + Cy) / 2} fontSize={12} textAnchor="middle" fill="#374151">b = {sideB.toFixed(2)}</text>
            <text x={(Ax + Bx) / 2 + 22} y={(Ay + By) / 2} fontSize={12} textAnchor="middle" fill="#374151">c = {sideC.toFixed(2)}</text>
        </svg>
    )
}
